import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState } from 'react'
import {
  X,
  History,
  Trophy,
  Video,
  FileText,
  Users,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { FieldSnap, TeamBlockCapture } from '../../types/fieldCapture'
import { RosterAthleteOption } from '../../types/sports'
import { formatFieldYard, formatNetYards } from './fieldYardageUtils'

interface SnapHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  capture: TeamBlockCapture | null
  snaps: FieldSnap[]
  onSelectSnap: (snap: FieldSnap) => void
  rosterAthletes?: RosterAthleteOption[]
}

const PRIMARY_RESULT_LABELS: Record<string, string> = {
  pass_complete: 'Pase Completo',
  pass_incomplete: 'Pase Incompleto',
  run: 'Carrera',
  sack: 'Sack',
  interception: 'Intercepción',
  penalty_offense: 'Falta Ataque',
  penalty_defense: 'Falta Defensa',
  conversion_good: 'Conversión Buena',
  conversion_failed: 'Conversión Fallida',
  other: 'Otro',
}

const SECONDARY_EVENT_LABELS: Record<string, string> = {
  flag_attempt_success: 'Flag Conseguido',
  flag_attempt_miss: 'Flag Fallado',
  pass_defended: 'Pase Defendido',
  sack: 'Sack',
  pressure: 'Presión / Hurry',
  touchdown: 'Touchdown (+6)',
  drop: 'Drop',
  conversion_attempt: 'Intento Conversión',
  conversion_success: 'Conversión Exitosa',
  penalty: 'Penalización',
}

export const SnapHistoryModal: React.FC<SnapHistoryModalProps> = ({
  isOpen,
  onClose,
  capture,
  snaps,
  onSelectSnap,
  rosterAthletes = [],
}) => {
  const [expandedParticipantsSnapId, setExpandedParticipantsSnapId] = useState<string | null>(null)

  useModalLifecycle(isOpen, onClose)

  if (!isOpen) return null

  const getAthleteName = (id?: string | null) => {
    if (!id) return null
    const found = rosterAthletes.find((a) => a.id === id)
    return found ? `${found.full_name} #${found.dorsal_number}` : 'Atleta'
  }

  const formatElapsed = (sec?: number) => {
    if (sec === undefined || sec === null) return '--:--'
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
        padding: '0.75rem',
        backdropFilter: 'blur(3px)',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: 'var(--radius-md)',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-card)',
          overflow: 'hidden',
          border: '1px solid var(--surface-border)',
        }}
      >
        {/* Cabecera Azul Noche */}
        <header
          style={{
            backgroundColor: 'var(--nav-bg)',
            color: '#fff',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--nav-border)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <History size={18} style={{ color: 'var(--brand-red)' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: '#fff' }}>
                Historial Snap a Snap — {capture?.title || 'Partido / Bloque'}
              </h3>
              <span style={{ fontSize: '0.68rem', color: 'var(--nav-text-muted)' }}>
                {snaps.length} snaps registrados · {capture?.status === 'completed' ? 'Finalizado' : 'En curso'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--nav-text-muted)',
              cursor: 'pointer',
              padding: '0.35rem',
            }}
            aria-label="Cerrar historial"
          >
            <X size={18} />
          </button>
        </header>

        {/* Resumen Superior si es Partido o Bloque */}
        {capture?.mode === 'match' && capture.match_context && (
          <div
            style={{
              backgroundColor: 'var(--nav-bg-surface)',
              color: '#fff',
              padding: '0.45rem 1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.78rem',
              borderBottom: '1px solid var(--nav-border)',
              flexShrink: 0,
            }}
          >
            <div>
              <span style={{ color: 'var(--brand-red)', fontWeight: 800 }}>España</span>{' '}
              <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.95rem' }}>
                {capture.match_context.our_score}
              </strong>{' '}
              —{' '}
              <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.95rem' }}>
                {capture.match_context.rival_score}
              </strong>{' '}
              <span style={{ fontWeight: 700 }}>{capture.match_context.rival_name || 'Rival'}</span>
            </div>
            <span style={{ color: 'var(--nav-text-muted)' }}>
              Periodo: {capture.match_context.period || '1ª Parte'}
            </span>
          </div>
        )}

        {/* Lista Cronológica de Snaps */}
        <div
          style={{
            padding: '0.75rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.65rem',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {snaps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              No hay snaps registrados en este bloque o partido.
            </div>
          ) : (
            snaps.map((snap) => {
              const isExpanded = expandedParticipantsSnapId === snap.id
              const activeEvents = (snap.events || []).filter((e) => !e.is_revoked)

              return (
                <div
                  key={snap.id}
                  style={{
                    backgroundColor: 'var(--surface-subtle)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.65rem 0.8rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease',
                    flexShrink: 0,
                    flexGrow: 0,
                    minHeight: 'fit-content',
                    boxSizing: 'border-box',
                    width: '100%',
                  }}
                  onClick={() => onSelectSnap(snap)}
                >
                  {/* Fila 1: Secuencia, Tiempo, Resultado y Down */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                      <span
                        style={{
                          backgroundColor: 'var(--nav-bg)',
                          color: '#fff',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          padding: '0.15rem 0.45rem',
                          borderRadius: '4px',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        Snap #{snap.sequence}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {formatElapsed(snap.elapsed_seconds_in_block)}
                      </span>

                      {/* Badge Validez */}
                      {snap.validity !== 'valid' && (
                        <span
                          className={`badge ${snap.validity === 'no_play' ? 'badge-warning' : 'badge-muted'}`}
                          style={{ fontSize: '0.62rem', padding: '0.1rem 0.35rem' }}
                        >
                          {snap.validity === 'no_play' ? 'No-Play' : 'Anulado'}
                        </span>
                      )}

                      {/* Badge Sincronización Local */}
                      {(snap as any).sync_status === 'local' && (
                        <span
                          className="badge badge-yellow"
                          style={{ fontSize: '0.62rem', padding: '0.1rem 0.35rem' }}
                          title="Guardado localmente en este dispositivo · Pendiente de sincronizar con el servidor"
                        >
                          Local · Pendiente
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      {/* Resultado Principal */}
                      <span
                        style={{
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          color: snap.is_touchdown ? '#d97706' : 'var(--brand-red)',
                        }}
                      >
                        {snap.primary_result ? PRIMARY_RESULT_LABELS[snap.primary_result] || snap.primary_result : 'Sin registrar'}
                      </span>

                      {/* Badge Touchdown */}
                      {snap.is_touchdown && (
                        <span
                          style={{
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #d97706',
                            fontSize: '0.65rem',
                            fontWeight: 800,
                            padding: '0.1rem 0.4rem',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.2rem',
                          }}
                        >
                          <Trophy size={11} />
                          TD (+6)
                        </span>
                      )}

                      {/* Badge Conversión */}
                      {snap.is_conversion && (
                        <span
                          style={{
                            backgroundColor: snap.conversion_result === 'successful' ? '#dcfce7' : '#fee2e2',
                            color: snap.conversion_result === 'successful' ? '#166534' : '#991b1b',
                            border: snap.conversion_result === 'successful' ? '1px solid #86efac' : '1px solid #fca5a5',
                            fontSize: '0.65rem',
                            fontWeight: 800,
                            padding: '0.1rem 0.4rem',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.2rem',
                          }}
                        >
                          Conv {snap.conversion_points_attempted || (snap.conversion_type === '2_points' ? 2 : 1)} pt:{' '}
                          {snap.conversion_result === 'successful'
                            ? `✓ (+${snap.points_awarded || snap.conversion_points_attempted || 1})`
                            : '✗ (0)'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Fila 2: Down, Posición de Campo y Yardas Netas */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      borderTop: '1px solid var(--surface-border)',
                      paddingTop: '0.35rem',
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Down & Posición: </span>
                      <strong>
                        {snap.down ? `${snap.down}º Down` : '1º Down'} ·{' '}
                        {formatFieldYard(snap.yard_line_start)}
                        {snap.yard_line_end !== null && snap.yard_line_end !== undefined
                          ? ` → ${formatFieldYard(snap.yard_line_end)}`
                          : ''}
                      </strong>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Ganancia:</span>
                      <strong
                        style={{
                          color:
                            snap.net_yards && snap.net_yards > 0
                              ? 'var(--status-success-text)'
                              : snap.net_yards && snap.net_yards < 0
                              ? 'var(--brand-red)'
                              : 'var(--text-main)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatNetYards(snap.net_yards)}
                      </strong>
                    </div>
                  </div>

                  {/* Fila 3: Llamadas Tácticas y Anotador */}
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    <span>
                      Llamadas: <strong>{snap.offense_call_actual || '(Ataque)'}</strong> vs{' '}
                      <strong>{snap.defense_call_actual || '(Defensa)'}</strong>
                    </span>

                    {/* Info Anotador y Lateral */}
                    {snap.is_touchdown && (
                      <div style={{ marginTop: '0.15rem', color: '#92400e', fontWeight: 600 }}>
                        Anotador: {getAthleteName(snap.scorer_athlete_id) || 'Asignado'}
                        {snap.had_lateral ? ' (Lateral / reparto pendiente)' : ' (Avance íntegro)'}
                      </div>
                    )}
                  </div>

                  {/* Fila 4: Eventos Secundarios (Chips) */}
                  {activeEvents.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.15rem' }}>
                      {activeEvents.map((ev, i) => {
                        const actor = (snap.participants || []).find((p) => p.athlete_id === ev.actor_athlete_id)
                        const label = SECONDARY_EVENT_LABELS[ev.event_type] || ev.event_type

                        return (
                          <span
                            key={i}
                            style={{
                              backgroundColor: 'var(--surface-card)',
                              border: '1px solid var(--surface-border)',
                              fontSize: '0.65rem',
                              padding: '0.1rem 0.35rem',
                              borderRadius: '4px',
                              color: 'var(--text-main)',
                            }}
                          >
                            <strong>{label}</strong>
                            {actor && `: ${actor.athlete_name} #${actor.dorsal_number}`}
                          </span>
                        )
                      })}
                    </div>
                  )}

                  {/* Fila 5: Participantes, Notas y Vídeo */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.68rem',
                      color: 'var(--text-muted)',
                      borderTop: '1px dashed var(--surface-border)',
                      paddingTop: '0.3rem',
                      marginTop: '0.2rem',
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedParticipantsSnapId(isExpanded ? null : snap.id)
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '0.68rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontWeight: 600,
                      }}
                    >
                      <Users size={12} />
                      <span>10 Participantes</span>
                      {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {snap.video_timestamp_mark && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                          <Video size={11} />
                          {snap.video_timestamp_mark}
                        </span>
                      )}
                      {snap.notes && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }} title={snap.notes}>
                          <FileText size={11} />
                          Nota
                        </span>
                      )}
                      <span style={{ color: 'var(--brand-red)', fontWeight: 600 }}>Inspeccionar →</span>
                    </div>
                  </div>

                  {/* Panel Desplegable de los 10 Participantes Inmutables */}
                  {isExpanded && (
                    <div
                      style={{
                        backgroundColor: 'var(--surface-card)',
                        padding: '0.45rem',
                        borderRadius: '4px',
                        border: '1px solid var(--surface-border)',
                        marginTop: '0.3rem',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.4rem',
                        fontSize: '0.66rem',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div>
                        <strong style={{ color: 'var(--brand-red)', display: 'block', marginBottom: '0.2rem' }}>
                          Ataque:
                        </strong>
                        {(snap.participants || [])
                          .filter((p) => p.unit === 'offense')
                          .map((p, idx) => (
                            <div key={idx}>
                              {p.position}: {p.athlete_name} #{p.dorsal_number}
                              {p.was_substituted && (
                                <span style={{ color: 'var(--brand-yellow)', marginLeft: '0.2rem' }}>
                                  (sust.)
                                </span>
                              )}
                            </div>
                          ))}
                      </div>

                      <div>
                        <strong style={{ color: 'var(--nav-bg)', display: 'block', marginBottom: '0.2rem' }}>
                          Defensa:
                        </strong>
                        {(snap.participants || [])
                          .filter((p) => p.unit === 'defense')
                          .map((p, idx) => (
                            <div key={idx}>
                              {p.position}: {p.athlete_name} #{p.dorsal_number}
                              {p.was_substituted && (
                                <span style={{ color: 'var(--brand-yellow)', marginLeft: '0.2rem' }}>
                                  (sust.)
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Pie del modal */}
        <footer
          style={{
            backgroundColor: 'var(--surface-subtle)',
            padding: '0.65rem 1rem',
            borderTop: '1px solid var(--surface-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Pulsa en cualquier snap para consultar y editar sus detalles o eventos.
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
          >
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  )
}
